<template>
  <div
    :class="[
      'menu-shelf critical-component-hide',
      `show-tier${currentTier}`,
      { [$class('active')]: isActive },
    ]"
    js-overlay="scrollElement"
  >
    <!-- Close icon -->
    <div class="menu-shelf__header">
      <transition
        name="fade-down"
        mode="out-in"
      >
        <button
          v-if="currentTier > 1"
          v-trigger-icon
          class="menu-shelf__back"
          :aria-label="`
            ${$string('general.menu_shelf.back_to')}
            ${$string('general.menu_shelf.top_level')}
          `"
          type="button"
          @click.prevent="() => currentLink = false"
        >
          <animated-icon
            :transition-on-hover="false"
            transition-type="swipe"
          >
            <template #iconOne>
              <icon-arrow-left />
            </template>

            <template #iconTwo>
              <icon-arrow-left />
            </template>
          </animated-icon>

          <span
            class="menu-shelf__back-label text-body-xs"
            v-text="currentLink.title"
          />
        </button>
      </transition>
    </div>

    <transition
      name="fade-up"
      mode="out-in"
      appear
    >
      <div class="menu-shelf__content">
        <div
          class="menu-shelf__navigation-wrapper"
          js-body-scroll-lock-ignore
        >
          <!-- Upper main navigation list -->
          <div class="menu-shelf__navigation">
            <nav
              class="menu-shelf__menus"
              :aria-label="$string('accessibility.sign_posts.nav_drawer_menus')"
            >
              <!-- Tier 1 -->
              <ul
                class="menu-shelf__menu menu-shelf__tier1"
                :class="{ [$class('active')]: getActiveState(false) }"
              >
                <li
                  v-for="(tier1, index) in activeNavigation"
                  :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
                  :key="tier1.handle"
                  class="menu-shelf__menu-item"
                >
                  <button
                    v-if="tier1.links.length"
                    v-trigger-icon
                    class="
                      menu-shelf__link
                      menu-shelf__button
                      text-body-xs
                    "
                    :tabindex="getActiveState(false) ? 0 : -1"
                    :aria-label="`${$string('accessibility.open')} ${tier1.title}`"
                    @click="handleLinkClick(tier1)"
                  >
                    {{ tier1.title }}
                    <animated-icon
                      :transition-on-hover="false"
                      transition-type="swipe-right"
                    >
                      <template #iconOne>
                        <icon-arrow-right />
                      </template>

                      <template #iconTwo>
                        <icon-arrow-right />
                      </template>
                    </animated-icon>
                  </button>

                  <a
                    v-else
                    class="menu-shelf__link text-body-xs"
                    :class="{'menu-shelf__link--is-sale' : addSaleHighlight && index === (activeNavigation.length - 1)}"
                    :href="tier1.localisedUrl"
                    :tabindex="getActiveState(false) ? 0 : -1"
                    v-html="tier1?.title"
                  />

                  <!-- Tier 2 -->
                  <ul
                    v-if="tier1.links.length"
                    class="menu-shelf__menu menu-shelf__tier2"
                    :class="{ 'is-active': getActiveState(tier1) || getChildActiveState(tier1), 'menu-shelf__tier2--multiple': currentLink.columnMultiple }"
                  >
                    <li
                      v-for="(tier2) in tier1.links"
                      :key="tier2.handle"
                      :ref="(args) => setItemRefs(args, {
                        handle: tier1.handle,
                        tier: 2,
                        type: 'link',
                      })"
                      class="menu-shelf__menu-item"
                    >
                      <menu-shelf-linklist
                        :linklist="tier2"
                      />
                    </li>
                  </ul>
                </li>

                <li
                  v-if="isCustomer && (eagleClubMemberLink.enabled && eagleClubMemberLink.url)"
                  class="menu-shelf__menu-item"
                >
                  <a
                    class="menu-shelf__link text-body-xs eagle-club-member-link"
                    :href="eagleClubMemberLink.url"
                  >
                    <span v-text="eagleClubMemberLink.label" />
                    <icon-eagle />
                  </a>
                </li>
              </ul>
            </nav>

            <transition
              name="fade-up-only"
              mode="out-in"
            >
              <div
                v-if="currentImages?.[getCurrentLinkReference]?.length"
                class="menu-shelf__nav-blocks"
              >
                <a
                  v-for="(block, index) in currentImages[getCurrentLinkReference]"
                  :key="index"
                  :href="block.link"
                  class="menu-shelf__nav-block"
                  :style="{
                    '--nav-block-overlay-color': block.overlayColor,
                    '--nav-block-overlay-opacity': block.overlayOpacity / 100,
                  }"
                >
                  <div class="menu-shelf__nav-block-holder">
                    <responsive-image
                      :key="`${getCurrentLinkReference}-${index}-${block.src}`"
                      :image="{ src: block.src }"
                      :alt="`${$string('accessibility.door_tile')} ${block.text}`"
                      block-class="menu-shelf__nav-block-image"
                    />
                    <div class="menu-shelf__nav-block-overlay" />
                  </div>

                  <div class="menu-shelf__nav-block-title-wrapper ">
                    <span
                      class="menu-shelf__nav-block-title text-body-s-bold"
                      :style="{ '--nav-block-text-color': block.textColor }"
                      v-text="block.text"
                    />
                  </div>
                </a>
              </div>
            </transition>
          </div>

          <a
            v-if="isActive"
            class="menu-shelf__footer-logo"
            :href="$variable('routes.root')"
            :aria-label="$string('general.site_header.return_home')"
          >
            <icon-logo />
          </a>

          <!-- Lower nav list -->
          <transition
            name="fade-up-only"
            mode="out-in"
          >
            <div
              v-if="currentTier === 1 && isMobile"
              class="menu-shelf__navigation-lower"
            >
              <nav class="menu-shelf__lower-menus">
                <div
                  ref="lowerMenu"
                  class="menu-shelf__menu"
                >
                  <ul>
                    <li
                      v-for="(link, index) in drawerLowerLinks"
                      :key="index"
                      class="menu-shelf__menu-item"
                    >
                      <a
                        class="menu-shelf__link text-body-xs"
                        :href="link.url"
                        :tabindex="currentTier === 1 ? 0 : -1"
                        v-text="link.title"
                      />
                    </li>
                    <li
                      class="menu-shelf__menu-item eagle-club-non-member-link"
                      v-if="!isCustomer && (eagleClubNonMemberLink.enabled && eagleClubNonMemberLink.url)"
                    >
                      <a
                        class="menu-shelf__link text-body-xs"
                        :href="eagleClubNonMemberLink.url"
                        :aria-label="eagleClubNonMemberLink.label"
                      >
                        <span class="text-body-2xs" v-text="eagleClubNonMemberLink.label" />
                        <icon-eagle />
                      </a>
                    </li>
                  </ul>

                  <a
                    class="menu-shelf__footer-logo-mobile"
                    :href="$variable('routes.root')"
                    :aria-label="$string('general.site_header.return_home')"
                  >
                    <icon-logo />
                  </a>

                  <ul class="menu-shelf__icon-links">
                    <li
                      v-if="showDrawerAccount"
                      class="menu-shelf__menu-item menu-shelf__item-icon"
                    >
                      <a
                        v-if="$variable('customer.loggedIn')"
                        class="menu-shelf__link text-body-xs"
                        :href="$variable('routes.account.logout')"
                        :tabindex="currentTier === 1 ? 0 : -1"
                      >
                        <icon-account />
                        <span v-text="$string('general.site_header.log_out')" />
                      </a>

                      <a
                        v-else
                        class="menu-shelf__link text-body-xs"
                        :href="$variable('routes.account.url')"
                        :tabindex="currentTier === 1 ? 0 : -1"
                      >
                        <icon-account />
                        <span v-text="$string('general.site_header.log_in_register')" />
                      </a>
                    </li>
                  </ul>
                </div>
              </nav>
            </div>
          </transition>

          <transition
            name="fade-up-only"
            mode="out-in"
          >
            <market-switcher-trigger
              v-if="currentTier === 1"
              class="menu-shelf__market-switcher"
              layout="large"
            />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Menu shelf
 * -----------------------------------------------------------------------------
 * Mobile menu shelf.
 * - CSS variable --menu-shelf-links-height is updated to match height of current
 *   visible links.
 *
 * @param {String} activeMenu - Name of the active menu.
 * @param {Array} drawerLowerLinks - Array of link objects.
 * @param {Array} drawerNavigation - Array of navigation objects.
 * @param {Array} images - Array of image objects.
 * @param {Boolean} isActive - If the containing overlay is active.
 * @param {Number} itemCount - Number of tier 1 items in linklist.
 * @param {String} namespace - Overlay namespace for the drawer.
 * @param {Boolean} addSaleHighlight - Decides whether to highlight the last link.
 * @param {Boolean} showDrawerAccount - Show the account link in the drawer.
 * @param {Array} navigation - Array of navigation objects.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconArrowLeft from '~icons/direction/arrow/left.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconAccount from '~icons/misc/account.svg'
import IconLogo from '~icons/misc/logo.svg'
import IconEagle from '~icons/misc/eagle.svg'

import AnimatedIcon from '~global/animated-icon/animated-icon'
import MarketSwitcherTrigger from '~async/market-switcher-trigger/market-switcher-trigger'
import MenuShelfLinklist from './menu-shelf-linklist'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'MenuShelf',

  components: {
    AnimatedIcon,
    IconAccount,
    IconLogo,
    IconArrowLeft,
    IconArrowRight,
    MarketSwitcherTrigger,
    MenuShelfLinklist,
    ResponsiveImage,
    IconEagle,
  },

  props: {
    activeMenu: {
      type: String,
      default: null,
    },
    drawerLowerLinks: {
      type: Array,
      default: () => [],
    },
    drawerNavigation: {
      type: Array,
      default: () => [],
    },
    eagleClubNonMemberLink: {
      type: Object,
      default: () => ({}),
    },
    eagleClubMemberLink: {
      type: Object,
      default: () => ({}),
    },
    images: {
      type: Array,
      default: () => [],
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    itemCount: {
      type: Number,
      default: 5,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    addSaleHighlight: {
      type: Boolean,
      default: false,
    },
    showDrawerAccount: {
      type: Boolean,
      default: true,
    },
    navigation: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['clear-active-menu'],

  data() {
    return {
      currentLink: false,
      currentHeight: false,
      itemRefs: [],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      isCustomer: (state) => state.index.isCustomer,
    }),

    /**
     * Returns the appropriate navigation based on device type
     * @returns {Array}
     */
    activeNavigation() {
      return this.isMobile ? this.drawerNavigation : this.navigation
    },

    /**
     * Finds and returns a useable reference for matching nav images with the
     * linklist they refer to.
     * @returns {String}
     */
    getCurrentLinkReference() {
      return (this?.currentLink?.title || "").toUpperCase()
    },

    /**
     * Format supplied images into a format that can be easily rendered when the
     * menu is built.
     * @returns {Object} - Object of arrays with linklist titles as keys.
     */
    currentImages() {
      if  (!this.images.length)  {
        return
      }

      const formatted = this.images.reduce((agg, item) => {
        const title = (item.title || "").toUpperCase()
        if(agg[title])  {
          agg[title].push(item.image)
        } else {
          agg[title] = [item.image]
        }

        return agg
      }, {})
      return formatted
    },

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },
  },

  watch: {
    activeMenu(val) {
      if (!val) {
        return
      }

      this.initDrawer()
    },

    /**
     * Watch the current tier change, which indicates the user has progressed
     * further into the menu. Use this to re-run the calculations needed for
     * building out the adaptive menu shelf.
     */
    currentTier: function()  {
      this.updateSizes()
      this.updateLowerMenuHeight()
    },

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.updateFocus()
      this.updateSizes()
      this.updateLowerMenuHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay(val) {
      this.updateLowerMenuHeight()
      if (val) { return }

      this.$emit('clear-active-menu')
      this.currentLink = false
    },

    /**
     * Watch isMobile to close the menu when switching from mobile to desktop
     */
    isMobile(newVal, oldVal) {
      if (!newVal && oldVal && this.isActive) {
        this.closeOverlay(this.namespace)
        this.$emit('clear-active-menu')
        this.currentLink = false
        return
      }

      this.updateSizes()
      this.updateLowerMenuHeight()
    },
  },

  mounted() {
    this.initDrawer()
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPlacements: 'nosto/setPlacements',
    }),

    /**
     * Handles drawer init functionality.
     */
    async initDrawer() {
      this.removeStaticMenu()
      this.updateFocus()

      if (this.activeMenu) {
        this.handleLinkClick({}, true)
      }

      this.updateSizes()
      this.updateLowerMenuHeight()
    },

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        return tier3Link.handle === this.currentLink.handle
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `link` or `back`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Removes static menu shelf rendered by menu-shelf.snippet.liquid.
     * - Otherwise there would be duplicate menus.
     */
    removeStaticMenu() {
      const container = document.querySelector('[js-menu-shelf="container"]')

      if (!container) {
        return
      }

      container.remove()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', 'menuShelf')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    updateSizes() {
      if(!this.isMobile) {
        return
      }

      nextTick(() => {
        let height = 0
        let width = 0

        /**
         * Detect if in tier 2 and up on desktop, as calculations for height are
         * instead taken as the height of the tallest element
         */
        if  (this.currentTier === 1 || this.isMobile)  {
          this.currentItemRefs.forEach((ref) => {
            if (!ref?.element?.offsetHeight) {
              return
            }

            /**
             * If on mobile and the current menu is displayed in multiple horizontal
             * columns then calculate the total height of the container instead.
             */
            width += ref.element.offsetWidth
            if  (this.currentLink.columnMultiple)  {
              height = ref.element.closest('.menu-shelf__menu').offsetHeight
            } else {
              height += ref.element.offsetHeight
            }
          })
        } else {
          const heightsArr = []

          this.currentItemRefs.forEach((ref) => {
            if (!ref?.element?.offsetHeight || !ref?.element?.offsetWidth) {
              return
            }
            heightsArr.push(ref.element.offsetHeight)
            width += ref.element.offsetWidth
          })

          height = heightsArr.sort((a, b) => a - b)[0]
        }

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if ((height === 0 || width === 0) && this.currentItemRefs.length) {
          setTimeout(() => {
            this.updateSizes()
            return
          }, 100)
        }

        /**
         * Set CSS variables for linklist height and width.
         */
        document.documentElement.style.setProperty(
          '--menu-shelf-links-height',
          `${height}px`,
        )

        document.documentElement.style.setProperty(
          '--menu-shelf-links-width',
          `${width}px`,
        )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

     /**
     * Update height of drawer lower navigation.
     * Gets the height of the rendered lower drawer menu items ul. Separated
     * out for readability as it sets a different css variable and does not require
     * to loop over anything.
     */
    updateLowerMenuHeight() {
      if(!this.isMobile) {
        return
      }

      nextTick(() => {
        const height = this.$refs.lowerMenu?.offsetHeight || 0

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if (height === 0 && this.drawerlowerLinks?.length) {
          setTimeout(() => {
            this.updateLowerMenuHeight()
            return
          }, 100)
        }

        /**
         * Set CSS variable.
         */
        document.documentElement.style.setProperty(
          '--lower-menu-shelf-links-height',
          `${height}px`,
        )
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     * @param {Boolean} [updateByProp]
     */
    handleLinkClick(link, updateByProp = false) {
      if  (!updateByProp)  {
        this.currentLink = link
        return
      }

      const active = this.navigation.find(item => item.title === this.activeMenu)
      if  (!active)  {
        return
      }
      this.currentLink = active
    },
  },
}
</script>

<style lang="scss">
@import './menu-shelf';
</style>
